import { AttorneyFeeSelect } from "@/components/features/matter/AttorneyFeeSelect/AttorneyFeeSelect";
import {
  formatAttorneyFee,
  isOneThirdAttorneyFee,
} from "@/lib/utils/attorneyFeeUtils";
import { useUser } from "@/components/UserProvider";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  InlineEditableField,
  InlineEditableNumberField,
  Input,
  Skeleton,
  Typography,
} from "@/components/ui";
import { retrieveMatterSettingsQueryOptions } from "@/lib/data/react-query/matters/matter-settings";
import { retrieveMatterStatusCategoriesQueryOptions } from "@/lib/data/react-query";
import {
  useCreateMatterStatus,
  useDeleteMatterStatus,
  useUpdateMatterStatus,
  useUpdateMatterStatusOrder,
} from "@/lib/data/react-query/matters/matter-status";
import { MatterStatusSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { serverErrorFallbackText } from "@/lib/data/static/fallbackMessages";
import { MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS } from "@/lib/data/static/matterStatics";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import { type MatterStatus, type MatterStatusCategory } from "@/lib/types";
import { ErrorBoundary } from "@sentry/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { sortBy, toLower } from "lodash";
import {
  Check,
  ChevronDownSquare,
  ChevronUpSquare,
  Trash2,
} from "lucide-react";
import pluralize from "pluralize";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const formSchema = MatterStatusSchema.pick({
  label: true,
  description: true,
  goal: true,
}).extend({
  categoryId: z.string().min(1, { message: "Category is required" }),
  defaultAttorneyFee: z
    .string()
    .optional()
    .refine(
      (value) => !value || (Number(value) >= 0 && Number(value) <= 100),
      "Enter a fee between 0 and 100",
    ),
});

const defaultFormValues = {
  label: "",
  description: "",
  categoryId: "pre-litigation" as const,
  goal: null,
  defaultAttorneyFee: "33.33",
};

const inlineFieldSchemas = {
  label: MatterStatusSchema.shape.label,
  description: MatterStatusSchema.shape.description,
  goal: MatterStatusSchema.shape.goal,
  fee: z
    .number()
    .min(0, "Enter a fee between 0 and 100")
    .max(100, "Enter a fee between 0 and 100")
    .nullable()
    .optional(),
};

type AddMatterStatusEditorProps = {
  matterStatuses: MatterStatus[];
  category: string;
  isReadOnly: boolean;
  onClose: () => void;
};

function AddMatterStatusEditor({
  matterStatuses,
  category,
  isReadOnly,
  onClose,
}: AddMatterStatusEditorProps) {
  const createMatterStatus = useCreateMatterStatus({ showToast: false });
  const isFormLoading = createMatterStatus.isPending;

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ...defaultFormValues,
      categoryId: category,
    },
    mode: "onSubmit",
  });

  useEffect(() => {
    form.reset({
      ...defaultFormValues,
      categoryId: category,
    });
  }, [category, form]);

  const createErrorMessage = useMemo(() => {
    const errors = [
      form.formState.errors.label?.message,
      form.formState.errors.description?.message,
      form.formState.errors.categoryId?.message,
      form.formState.errors.goal?.message,
      form.formState.errors.defaultAttorneyFee?.message,
    ].filter(Boolean);

    return errors.length > 0 ? String(errors[0]) : null;
  }, [form.formState.errors]);

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (
      matterStatuses.some(
        (matterStatus) => toLower(matterStatus.label) === toLower(data.label),
      )
    ) {
      form.setError("label", {
        message: "Matter status label must be unique",
      });
      return;
    }

    createMatterStatus.mutate({
      ...data,
      defaultAttorneyFee: Number(data.defaultAttorneyFee),
    });
    onClose();
    form.reset(defaultFormValues);
  };

  return (
    <div className="space-y-3">
      <Form {...form}>
        <form
          className="relative w-full space-y-3"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <fieldset
            disabled={isReadOnly || isFormLoading}
            className="bg-muted/60 flex w-full flex-col gap-2 rounded-xl px-2 py-1.5 lg:flex-row lg:items-center lg:justify-between"
          >
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <FormField
                  control={form.control}
                  name="label"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="New status label"
                          className="border-border/40 h-9 w-40 bg-white px-2 py-0.5 text-sm leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem className="min-w-0 space-y-0">
                      <FormControl>
                        <Input
                          {...field}
                          value={field.value ?? ""}
                          placeholder="Add description"
                          className="border-border/40 h-9 w-60 bg-white px-2 py-0.5 text-xs leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              {createErrorMessage ? (
                <Typography className="text-destructive text-xs">
                  {createErrorMessage}
                </Typography>
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-2 lg:flex-nowrap">
              <div className="text-muted-foreground flex h-9 items-center gap-1 rounded-md px-2 text-xs">
                <span>Goal:</span>
                <FormField
                  control={form.control}
                  name="goal"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <Input
                          type="number"
                          inputMode="decimal"
                          value={field.value ?? ""}
                          placeholder="Days"
                          min={0}
                          step={1}
                          onChange={(event) => {
                            const nextValue = event.target.value;
                            field.onChange(
                              nextValue === "" ? null : Number(nextValue),
                            );
                          }}
                          className="border-border/40 h-9 w-16 bg-white px-2 py-0.5 text-xs leading-normal focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <div className="text-muted-foreground flex h-9 items-center gap-1 rounded-md px-2 text-xs">
                <span>Fee:</span>
                <FormField
                  control={form.control}
                  name="defaultAttorneyFee"
                  render={({ field }) => (
                    <FormItem className="space-y-0">
                      <FormControl>
                        <AttorneyFeeSelect
                          value={field.value ?? ""}
                          placeholder="33 1/3"
                          options={MATTER_DEFAULT_ATTORNEY_FEE_OPTIONS}
                          onChange={field.onChange}
                          ariaLabel="Default attorney fee (%)"
                          className="min-w-28"
                        />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </div>
              <Button
                type="submit"
                variant="ghost"
                size="xs"
                disabled={isReadOnly || isFormLoading}
                aria-label="Save new status"
              >
                <Check />
              </Button>
            </div>
          </fieldset>

          <button type="submit" className="hidden" aria-hidden="true" />
        </form>
      </Form>
    </div>
  );
}

function MatterStatusesListDangerous() {
  const [activeStatusId, setActiveStatusId] = useState<string | null>(null);
  const [activeField, setActiveField] = useState<
    "label" | "description" | "goal" | "fee" | null
  >(null);
  const [inlineValue, setInlineValue] = useState("");
  const [inlineNumberValue, setInlineNumberValue] = useState<number | null>(
    null,
  );
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteStatus, setPendingDeleteStatus] =
    useState<MatterStatus | null>(null);
  const { hasActionPermission } = useUser();

  const {
    data: matterSettingsData,
    isLoading: isMatterSettingsLoading,
    isError: isMatterSettingsError,
  } = useQuery(retrieveMatterSettingsQueryOptions());
  const {
    data: matterStatusCategoriesData,
    isLoading: isMatterStatusCategoriesLoading,
    isError: isMatterStatusCategoriesError,
  } = useQuery(retrieveMatterStatusCategoriesQueryOptions());

  const updateStatusOrder = useUpdateMatterStatusOrder({ showToast: false });
  const deleteMatterStatus = useDeleteMatterStatus({ showToast: false });
  const editMatterStatus = useUpdateMatterStatus({ showToast: false });

  const canCreate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.create,
  );
  const canUpdate = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.update,
  );
  const canDelete = hasActionPermission(
    ACTION_PERMISSION_KEYS.matterStatus,
    ACTION_PERMISSION_VALUES.delete,
  );

  const matterStatuses = useMemo(
    () =>
      sortBy(matterSettingsData?.data.statuses ?? [], (matterStatus) => {
        const categoryIndex = (
          matterStatusCategoriesData?.data ?? []
        ).findIndex((category) => category.id === matterStatus.categoryId);
        return categoryIndex;
      }),
    [matterSettingsData, matterStatusCategoriesData?.data],
  );
  const categoriesByCount = useMemo<MatterStatusCategory[]>(() => {
    const categoryCounts = (matterStatusCategoriesData?.data ?? []).map(
      (category, index) => ({
        category,
        count: matterStatuses.filter(
          (status) => status.categoryId === category.id,
        ).length,
        index,
      }),
    );

    return categoryCounts
      .sort((a, b) => {
        if (b.count !== a.count) return b.count - a.count;
        return a.index - b.index;
      })
      .map(({ category }) => category);
  }, [matterStatusCategoriesData?.data, matterStatuses]);

  const closeEditor = () => {
    setActiveStatusId(null);
    setActiveField(null);
    setInlineValue("");
    setInlineNumberValue(null);
    setInlineError(null);
  };

  const startInlineEdit = (
    status: MatterStatus,
    field: "label" | "description" | "goal" | "fee",
  ) => {
    if (!canUpdate) return;
    setActiveStatusId(status.id);
    setActiveField(field);
    if (field === "goal") {
      setInlineNumberValue(status.goal ?? null);
      setInlineValue("");
    } else if (field === "fee") {
      setInlineValue(
        status.defaultAttorneyFee == null
          ? ""
          : formatAttorneyFee(status.defaultAttorneyFee),
      );
      setInlineNumberValue(null);
    } else {
      setInlineValue(
        field === "label" ? status.label : (status.description ?? ""),
      );
      setInlineNumberValue(null);
    }
    setInlineError(null);
  };

  const submitInlineEdit = (status: MatterStatus) => {
    if (!activeField) return;
    if (!canUpdate) {
      closeEditor();
      return;
    }

    const rawNextValue =
      activeField === "goal"
        ? inlineNumberValue
        : activeField === "fee"
          ? inlineValue.trim() === ""
            ? null
            : isOneThirdAttorneyFee(inlineValue)
              ? 33.33
              : Number(inlineValue)
          : inlineValue;
    const validation = inlineFieldSchemas[activeField].safeParse(rawNextValue);
    if (!validation.success) {
      setInlineError(validation.error.errors[0]?.message ?? "Invalid value");
      return;
    }

    const nextValue = validation.data;
    const currentValue =
      activeField === "label"
        ? status.label
        : activeField === "description"
          ? (status.description ?? "")
          : activeField === "goal"
            ? (status.goal ?? null)
            : (status.defaultAttorneyFee ?? null);

    if (nextValue === currentValue) {
      closeEditor();
      return;
    }

    if (
      activeField === "label" &&
      matterStatuses.some(
        (matterStatus) =>
          toLower(matterStatus.label) === toLower(nextValue as string) &&
          matterStatus.id !== status.id,
      )
    ) {
      setInlineError("Matter status label must be unique");
      return;
    }

    editMatterStatus.mutate({
      id: status.id,
      label: activeField === "label" ? (nextValue as string) : status.label,
      description:
        activeField === "description"
          ? (nextValue as string)
          : (status.description ?? undefined),
      categoryId: status.categoryId,
      goal:
        activeField === "goal"
          ? (nextValue as number | null)
          : (status.goal ?? null),
      defaultAttorneyFee:
        activeField === "fee"
          ? (nextValue as number | null)
          : (status.defaultAttorneyFee ?? null),
      dateModified: status.dateModified,
    });
    closeEditor();
  };

  const handleInlineKeyDown = (
    event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
    status: MatterStatus,
  ) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submitInlineEdit(status);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeEditor();
    }
  };

  const swapStatusOrder = (
    matterStatus: MatterStatus,
    direction: "up" | "down",
  ) => {
    const matterStatusesFiltered = sortBy(
      matterSettingsData?.data.statuses.filter(
        (status) => status.categoryId === matterStatus.categoryId,
      ) ?? [],
      [(status) => status.order ?? Number.POSITIVE_INFINITY, "label", "id"],
    );
    const matterStatusIndex = matterStatusesFiltered.findIndex(
      (status) => status.id === matterStatus.id,
    );
    const targetIndex =
      direction === "up" ? matterStatusIndex - 1 : matterStatusIndex + 1;
    if (matterStatusIndex >= 0 && matterStatusesFiltered[targetIndex]) {
      const reorderedStatuses = [...matterStatusesFiltered];
      const [movedStatus] = reorderedStatuses.splice(matterStatusIndex, 1);
      reorderedStatuses.splice(targetIndex, 0, movedStatus);
      updateStatusOrder.mutate({
        categoryId: matterStatus.categoryId,
        statuses: reorderedStatuses.map((status, index) => ({
          id: status.id,
          order: index + 1,
          dateModified: status.dateModified,
        })),
      });
    }
  };

  const renderStatusBadges = (status: MatterStatus) => {
    const isEditingGoal =
      activeStatusId === status.id && activeField === "goal";
    const isEditingFee = activeStatusId === status.id && activeField === "fee";
    const goalLabel = status.goal
      ? pluralize("day", status.goal, true)
      : "No goal set";
    const feeLabel = status.defaultAttorneyFee
      ? `${formatAttorneyFee(status.defaultAttorneyFee)}%`
      : "No default fee";

    return (
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="secondary" className="text-xs">
          <span className="mr-1">Goal:</span>
          <InlineEditableNumberField
            isEditing={isEditingGoal}
            value={inlineNumberValue}
            displayValue={goalLabel}
            canEdit={canUpdate}
            ariaLabel="Matter status goal"
            inputWidthClass="w-auto max-w-full"
            autoSize
            textClassName="text-xs"
            min={0}
            step={1}
            onStartEdit={() => {
              startInlineEdit(status, "goal");
            }}
            onChange={(value: number | null) => {
              setInlineNumberValue(value);
              if (inlineError) setInlineError(null);
            }}
            onSubmit={() => {
              submitInlineEdit(status);
            }}
            onKeyDown={(
              event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              handleInlineKeyDown(event, status);
            }}
          />
        </Badge>
        <Badge variant="secondary" className="text-xs">
          <span className="mr-1">Fee:</span>
          <InlineEditableField
            isEditing={isEditingFee}
            value={inlineValue}
            displayValue={feeLabel}
            canEdit={canUpdate}
            ariaLabel="Matter status fee"
            inputWidthClass="w-auto max-w-full"
            autoSize
            textClassName="text-xs"
            onStartEdit={() => {
              startInlineEdit(status, "fee");
            }}
            onChange={(value: string) => {
              setInlineValue(value);
              if (inlineError) setInlineError(null);
            }}
            onSubmit={() => {
              submitInlineEdit(status);
            }}
            onKeyDown={(
              event: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
            ) => {
              handleInlineKeyDown(event, status);
            }}
          />
        </Badge>
      </div>
    );
  };

  return (
    <div className="space-y-5">
      <ActionConfirmationModal
        title={`Are you sure you want to delete this matter status?`}
        open={deleteModalOpen}
        setOpen={setDeleteModalOpen}
        afterActionFn={async () => {
          if (!pendingDeleteStatus) return;
          await deleteMatterStatus.mutateAsync({
            id: pendingDeleteStatus.id,
          });
          setDeleteModalOpen(false);
          setPendingDeleteStatus(null);
        }}
        onCancel={() => {
          setPendingDeleteStatus(null);
        }}
      />

      {isMatterSettingsLoading || isMatterStatusCategoriesLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-20 w-full rounded-2xl" />
        </div>
      ) : isMatterSettingsError || isMatterStatusCategoriesError ? (
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      ) : (
        <div className="space-y-5">
          {categoriesByCount.map((category) => {
            const statuses = matterStatuses.filter(
              (status) => status.categoryId === category.id,
            );

            return (
              <section key={category.id} className="space-y-2">
                <div className="flex items-center justify-between">
                  <Typography weight="bold" size="sm">
                    {category.label}
                  </Typography>
                  <Typography className="text-muted-foreground text-xs">
                    {statuses.length} status
                    {statuses.length === 1 ? "" : "es"}
                  </Typography>
                </div>
                <div className="border-border/40 ml-1 space-y-2 border-l pt-1 pl-3">
                  {statuses.length === 0 ? (
                    <div className="text-muted-foreground text-xs">
                      No statuses yet.
                    </div>
                  ) : null}
                  {statuses.map((status, statusIndex) => {
                    const isEditingLabel =
                      activeStatusId === status.id && activeField === "label";
                    const isEditingDescription =
                      activeStatusId === status.id &&
                      activeField === "description";

                    return (
                      <div
                        key={status.id}
                        className="hover:bg-muted/30 focus-visible:ring-primary flex flex-col gap-2 rounded-xl px-2 py-1.5 transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden sm:flex-row sm:items-center sm:justify-between"
                      >
                        <div className="min-w-0 flex-1">
                          <Typography
                            tag="h4"
                            weight="semibold"
                            className={
                              isEditingLabel || isEditingDescription
                                ? "flex items-center gap-2 text-sm"
                                : "flex items-center gap-2 text-sm"
                            }
                          >
                            <span className="text-foreground flex-none">
                              <InlineEditableField
                                isEditing={isEditingLabel}
                                value={inlineValue}
                                displayValue={status.label}
                                canEdit={canUpdate}
                                ariaLabel="Matter status label"
                                inputWidthClass="w-auto max-w-full"
                                autoSize
                                matchDisplaySize
                                onStartEdit={() => {
                                  startInlineEdit(status, "label");
                                }}
                                onChange={(value: string) => {
                                  setInlineValue(value);
                                  if (inlineError) setInlineError(null);
                                }}
                                onSubmit={() => {
                                  submitInlineEdit(status);
                                }}
                                onKeyDown={(
                                  event: KeyboardEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                  >,
                                ) => {
                                  handleInlineKeyDown(event, status);
                                }}
                              />
                            </span>
                            <span className="text-muted-foreground min-w-0 flex-1 text-xs leading-snug">
                              <InlineEditableField
                                isEditing={isEditingDescription}
                                value={inlineValue}
                                displayValue={
                                  status.description
                                    ? status.description
                                    : "No description"
                                }
                                canEdit={canUpdate}
                                ariaLabel="Matter status description"
                                inputWidthClass="w-auto max-w-full"
                                autoSize
                                textClassName="text-xs"
                                matchDisplaySize
                                onStartEdit={() => {
                                  startInlineEdit(status, "description");
                                }}
                                onChange={(value: string) => {
                                  setInlineValue(value);
                                  if (inlineError) setInlineError(null);
                                }}
                                onSubmit={() => {
                                  submitInlineEdit(status);
                                }}
                                onKeyDown={(
                                  event: KeyboardEvent<
                                    HTMLInputElement | HTMLTextAreaElement
                                  >,
                                ) => {
                                  handleInlineKeyDown(event, status);
                                }}
                              />
                            </span>
                          </Typography>
                          {activeStatusId === status.id && inlineError ? (
                            <Typography className="text-destructive text-xs">
                              {inlineError}
                            </Typography>
                          ) : null}
                        </div>
                        <div className="flex flex-wrap items-center justify-between gap-3 sm:flex-nowrap sm:justify-end">
                          {renderStatusBadges(status)}
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="xs"
                              disabled={!canDelete}
                              onClick={() => {
                                setPendingDeleteStatus(status);
                                setDeleteModalOpen(true);
                              }}
                              aria-label={`Delete ${status.label}`}
                            >
                              <Trash2 />
                            </Button>
                            <Button
                              variant="ghost"
                              size="xs"
                              disabled={
                                updateStatusOrder.isPending ||
                                statusIndex === 0 ||
                                !canUpdate
                              }
                              onClick={() => {
                                swapStatusOrder(status, "up");
                              }}
                            >
                              <ChevronUpSquare />
                            </Button>
                            <Button
                              variant="ghost"
                              size="xs"
                              disabled={
                                updateStatusOrder.isPending ||
                                statusIndex === statuses.length - 1 ||
                                !canUpdate
                              }
                              onClick={() => {
                                swapStatusOrder(status, "down");
                              }}
                            >
                              <ChevronDownSquare />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {canCreate ? (
                    <AddMatterStatusEditor
                      matterStatuses={matterStatuses}
                      category={category.id}
                      isReadOnly={!canUpdate}
                      onClose={closeEditor}
                    />
                  ) : null}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function MatterStatusesList() {
  return (
    <ErrorBoundary
      fallback={
        <Typography variant="subHeading">{serverErrorFallbackText}</Typography>
      }
    >
      <MatterStatusesListDangerous />
    </ErrorBoundary>
  );
}
